import { useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Money } from '@rental/shared';
import { api } from '../lib/api.js';
import { AttachmentField } from '../components/Attachment.jsx';
import { Badge, Button, Card, Empty, Field, Page, Table, TextInput } from '../components/ui.jsx';

interface Provider {
  code: string;
  displayName: string;
  capabilities: { hasApi: boolean; needsCaptcha: boolean; needsMfa: boolean; supportsAutoFetch: boolean };
}
interface Connection {
  id: string;
  propertyId: string | null;
  providerCode: string;
  consumerNumber: string;
  hasCredentials: boolean;
  billingCycleDay: number | null;
  isActive: boolean;
  lastFetchAt: number | null;
  lastFetchStatus: 'ok' | 'failed' | 'manual_only' | 'needs_attention' | null;
  lastFetchError: string | null;
}
interface Bill {
  id: string;
  connectionId: string;
  periodStart: string;
  periodEnd: string;
  billNumber: string | null;
  dueDate: string | null;
  totalAmount: number;
  paymentStatus: 'paid' | 'unpaid' | 'unknown';
  source: 'api' | 'scrape' | 'manual';
  attachmentId: string | null;
}

export function ElectricityPage() {
  const qc = useQueryClient();
  const [showConn, setShowConn] = useState(false);
  const [showManual, setShowManual] = useState<string | null>(null);

  const providersQ = useQuery({
    queryKey: ['electricity-providers'],
    queryFn: () => api<{ providers: Provider[] }>('/api/v1/electricity/providers'),
  });
  const connsQ = useQuery({
    queryKey: ['electricity-connections'],
    queryFn: () => api<{ connections: Connection[] }>('/api/v1/electricity/connections'),
  });
  const billsQ = useQuery({
    queryKey: ['electricity-bills'],
    queryFn: () => api<{ bills: Bill[] }>('/api/v1/electricity/bills'),
  });

  const fetchNow = useMutation({
    mutationFn: (id: string) => api(`/api/v1/electricity/connections/${id}/fetch-now`, { method: 'POST', body: {} }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['electricity-connections'] });
      void qc.invalidateQueries({ queryKey: ['electricity-bills'] });
    },
  });

  const connName = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of connsQ.data?.connections ?? []) m.set(c.id, `${c.providerCode}/${c.consumerNumber}`);
    return m;
  }, [connsQ.data]);

  return (
    <Page
      title="Electricity"
      actions={<Button onClick={() => setShowConn((v) => !v)}>{showConn ? 'Cancel' : '+ New connection'}</Button>}
    >
      {showConn && (
        <div className="mb-4">
          <ConnectionForm
            providers={providersQ.data?.providers ?? []}
            onSaved={() => { setShowConn(false); void qc.invalidateQueries({ queryKey: ['electricity-connections'] }); }}
          />
        </div>
      )}

      <section className="mb-6">
        <h2 className="mb-2 text-lg font-medium">Connections</h2>
        {!connsQ.data?.connections.length ? (
          <Empty>No electricity connections yet.</Empty>
        ) : (
          <Table>
            <thead className="text-left text-slate-500 text-xs">
              <tr>
                <th className="border-b px-2 py-1 dark:border-slate-700">Provider</th>
                <th className="border-b px-2 py-1 dark:border-slate-700">Consumer no.</th>
                <th className="border-b px-2 py-1 dark:border-slate-700">Creds</th>
                <th className="border-b px-2 py-1 dark:border-slate-700">Last fetch</th>
                <th className="border-b px-2 py-1 dark:border-slate-700"></th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {connsQ.data.connections.map((c) => (
                <tr key={c.id} className="border-b border-slate-100 dark:border-slate-800">
                  <td className="px-2 py-1">{c.providerCode}</td>
                  <td className="px-2 py-1 font-mono text-xs">{c.consumerNumber}</td>
                  <td className="px-2 py-1">
                    {c.hasCredentials ? <Badge tone="good">Stored</Badge> : <Badge>None</Badge>}
                  </td>
                  <td className="px-2 py-1">
                    <FetchStatus status={c.lastFetchStatus} at={c.lastFetchAt} err={c.lastFetchError} />
                  </td>
                  <td className="px-2 py-1 text-right">
                    <div className="flex justify-end gap-2">
                      <Button variant="secondary" disabled={fetchNow.isPending} onClick={() => fetchNow.mutate(c.id)}>
                        {fetchNow.isPending ? 'Fetching…' : 'Fetch now'}
                      </Button>
                      <Button variant="secondary" onClick={() => setShowManual(showManual === c.id ? null : c.id)}>
                        Manual bill
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        {showManual && (
          <div className="mt-3">
            <ManualBillForm
              connectionId={showManual}
              onSaved={() => {
                setShowManual(null);
                void qc.invalidateQueries({ queryKey: ['electricity-bills'] });
              }}
            />
          </div>
        )}
      </section>

      <section>
        <h2 className="mb-2 text-lg font-medium">Bills</h2>
        {!billsQ.data?.bills.length ? (
          <Empty>No bills yet.</Empty>
        ) : (
          <Table>
            <thead className="text-left text-slate-500 text-xs">
              <tr>
                <th className="border-b px-2 py-1 dark:border-slate-700">Period</th>
                <th className="border-b px-2 py-1 dark:border-slate-700">Connection</th>
                <th className="border-b px-2 py-1 dark:border-slate-700">Bill no.</th>
                <th className="border-b px-2 py-1 dark:border-slate-700">Due</th>
                <th className="border-b px-2 py-1 dark:border-slate-700 text-right">Total</th>
                <th className="border-b px-2 py-1 dark:border-slate-700">Source</th>
                <th className="border-b px-2 py-1 dark:border-slate-700">Status</th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {billsQ.data.bills.map((b) => (
                <tr key={b.id} className="border-b border-slate-100 dark:border-slate-800">
                  <td className="px-2 py-1 whitespace-nowrap">{b.periodStart} → {b.periodEnd}</td>
                  <td className="px-2 py-1">{connName.get(b.connectionId) ?? b.connectionId.slice(-6)}</td>
                  <td className="px-2 py-1">{b.billNumber ?? '—'}</td>
                  <td className="px-2 py-1">{b.dueDate ?? '—'}</td>
                  <td className="px-2 py-1 text-right">{Money.fromPaise(b.totalAmount).format()}</td>
                  <td className="px-2 py-1"><Badge>{b.source}</Badge></td>
                  <td className="px-2 py-1">
                    <Badge tone={b.paymentStatus === 'paid' ? 'good' : b.paymentStatus === 'unpaid' ? 'warn' : 'neutral'}>
                      {b.paymentStatus}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </section>
    </Page>
  );
}

function FetchStatus({ status, at, err }: { status: Connection['lastFetchStatus']; at: number | null; err: string | null }) {
  if (!status) return <span className="text-xs text-slate-400">Never</span>;
  const tone = status === 'ok' ? 'good' : status === 'manual_only' ? 'neutral' : status === 'needs_attention' ? 'warn' : 'bad';
  return (
    <div className="flex flex-col">
      <Badge tone={tone}>{status}</Badge>
      {at && <span className="mt-0.5 text-[10px] text-slate-500">{new Date(at).toLocaleString()}</span>}
      {err && <span className="mt-0.5 text-[10px] text-red-600">{err.slice(0, 80)}</span>}
    </div>
  );
}

function ConnectionForm({ providers, onSaved }: { providers: Provider[]; onSaved: () => void }) {
  const [providerCode, setProviderCode] = useState(providers[0]?.code ?? 'manual');
  const [consumerNumber, setConsumerNumber] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [billingCycleDay, setBillingCycleDay] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api('/api/v1/electricity/connections', { method: 'POST', body }),
    onSuccess: onSaved,
    onError: (e) => setError(e instanceof Error ? e.message : 'Save failed'),
  });

  const provider = providers.find((p) => p.code === providerCode);
  const wantsCreds = !!provider?.capabilities.supportsAutoFetch && provider.code !== 'mock';

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    mutation.mutate({
      providerCode,
      consumerNumber,
      billingCycleDay: billingCycleDay ? Number(billingCycleDay) : undefined,
      credentials: wantsCreds && (username || password) ? { username, password } : undefined,
    });
  }

  return (
    <Card>
      <form onSubmit={onSubmit} className="grid gap-3 md:grid-cols-4 md:items-end">
        <Field label="Provider">
          <select
            value={providerCode} onChange={(e) => setProviderCode(e.target.value)}
            className="w-full rounded border border-slate-300 bg-white px-3 py-2 dark:border-slate-600 dark:bg-slate-800"
          >
            {providers.map((p) => (<option key={p.code} value={p.code}>{p.displayName}</option>))}
          </select>
        </Field>
        <Field label="Consumer no.">
          <TextInput required value={consumerNumber} onChange={(e) => setConsumerNumber(e.target.value)} />
        </Field>
        <Field label="Billing cycle day" hint="1–28">
          <TextInput type="number" min={1} max={28} value={billingCycleDay} onChange={(e) => setBillingCycleDay(e.target.value)} />
        </Field>
        {wantsCreds && (
          <>
            <Field label="Portal username">
              <TextInput autoComplete="off" value={username} onChange={(e) => setUsername(e.target.value)} />
            </Field>
            <Field label="Portal password" hint="Encrypted at rest">
              <TextInput type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />
            </Field>
          </>
        )}
        <div className="md:col-span-4 flex items-center justify-end gap-2">
          {error && <span className="text-xs text-red-600">{error}</span>}
          <Button type="submit" disabled={mutation.isPending}>Save connection</Button>
        </div>
      </form>
    </Card>
  );
}

function ManualBillForm({ connectionId, onSaved }: { connectionId: string; onSaved: () => void }) {
  const now = new Date();
  const firstOfPrev = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
  const lastOfPrev = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 0));
  const [periodStart, setPeriodStart] = useState(firstOfPrev.toISOString().slice(0, 10));
  const [periodEnd, setPeriodEnd] = useState(lastOfPrev.toISOString().slice(0, 10));
  const [billNumber, setBillNumber] = useState('');
  const [total, setTotal] = useState('');
  const [attachmentId, setAttachmentId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api('/api/v1/electricity/bills/manual', { method: 'POST', body }),
    onSuccess: onSaved,
    onError: (e) => setError(e instanceof Error ? e.message : 'Save failed'),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const paise = Money.parse(total).toPaise();
      mutation.mutate({
        connectionId,
        periodStart,
        periodEnd,
        billNumber: billNumber || undefined,
        totalAmountPaise: paise,
        attachmentId: attachmentId || undefined,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid amount');
    }
  }

  return (
    <Card>
      <form onSubmit={onSubmit} className="grid gap-3 md:grid-cols-6 md:items-end">
        <Field label="Period start"><TextInput type="date" required value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} /></Field>
        <Field label="Period end"><TextInput type="date" required value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} /></Field>
        <Field label="Bill number"><TextInput value={billNumber} onChange={(e) => setBillNumber(e.target.value)} /></Field>
        <Field label="Total (₹)"><TextInput required value={total} onChange={(e) => setTotal(e.target.value)} /></Field>
        <div className="md:col-span-2 flex items-end">
          <AttachmentField entityType="electricity_bill" value={attachmentId} onChange={setAttachmentId} />
        </div>
        <div className="md:col-span-6 flex items-center justify-end gap-2">
          {error && <span className="text-xs text-red-600">{error}</span>}
          <Button type="submit" disabled={mutation.isPending}>Save bill</Button>
        </div>
      </form>
    </Card>
  );
}
